import type { DocumentDto } from '@/api/types';

/**
 * WHICH DOCUMENT DOES "SIGN" OR "PRINT" ACT ON?
 *
 * ---- ⭐ WHY THIS IS A FILE AND NOT THREE if-STATEMENTS -----------------------
 *
 * Pete, 2026-08-25: *"If there is only 1 doc associated to the driver, he should
 * be able to hit a sign icon next to his name... Should print the original if
 * its the only one that exists and should print the signed copy... The scheduler
 * could select the eye icon for other options."* And then: *"Should be the same
 * options on an appointment if the scheduler decides to right-click and sign or
 * right-click and print. Same in edit screen."*
 *
 * ⛔ THREE CALL SITES MEANS ONE ANSWER, OR IT WILL DRIFT SILENTLY. The driver
 * card, the appointment's right-click menu and the edit screen all ask the same
 * question, and a scheduler who learns "the printer icon does the right thing"
 * in one place must not find it behaving differently three feet away. Nothing
 * would fail if they disagreed — it would just quietly print the wrong paper.
 *
 * ⭐ SO IT IS A PURE JUDGEMENT LAYER (arch §1O), the same shape as
 * `licenceCheck.ts`: documents in, a decision out. No fetch, no React, no
 * database — which is what makes it exhaustively testable, and the reason the
 * hard cases below are cheap to prove rather than expensive to argue about.
 *
 * ---- ⛔ THE RULE IS "ONE ORIGINAL", NEVER "ONE DOCUMENT" --------------------
 *
 * ⚠️ THIS IS THE WHOLE TRAP. A signed copy is its OWN row, pointing back at the
 * original through `signedFromDocumentId` (#110). So counting documents means
 * that the instant a scheduler signs the single document, the count becomes TWO
 * and the shortcut stops acting directly.
 *
 * ⛔ THE BUTTON WOULD WORK EXACTLY ONCE AND THEN CHANGE BEHAVIOUR — which is
 * worse than never having worked, because the scheduler has already learned to
 * trust it. Count originals; the signed copies hanging off them add no choice.
 *
 * ⭐ AND THE PRINT RULE THEN FALLS OUT OF THE SAME COUNT rather than being a
 * special case: one original with no signed copy prints the original; one
 * original with a signed copy prints the signed copy. Exactly what Pete
 * described, read once.
 */

export interface PaperworkChoice {
    /**
     * The single original to sign, or null when there is a real choice to make.
     * ⚠️ Always the ORIGINAL, never a signed copy — signing a signed copy would
     * stamp a stamp.
     */
    signTarget: DocumentDto | null;
    /**
     * What a Print press should act on: the newest signed copy when one exists,
     * otherwise the original. Null when there is a choice to make.
     */
    printTarget: DocumentDto | null;
    /**
     * ⛔ TRUE MEANS SHOW THE LIST INSTEAD OF GUESSING. Nought or several
     * originals are both "the scheduler has to choose", and a screen that makes
     * somebody pick from a list of one is a screen they learn to click through
     * without reading.
     */
    needsTheList: boolean;
    /** How many originals there are — for a tooltip that can say so. */
    originals: number;
    /** True when the one original has already been signed at least once. */
    alreadySigned: boolean;
}

/** ⚠️ A voided document is one somebody explicitly removed. It is not a choice. */
const live = (d: DocumentDto): boolean => !d.voidedUtc && !d.purged;

/** An original is a document that is not a signed copy OF something. */
const isOriginal = (d: DocumentDto): boolean => !d.signedFromDocumentId;

export function choosePaperworkAction(docs: DocumentDto[] | null | undefined): PaperworkChoice {
    const all = (docs ?? []).filter(live);
    const originals = all.filter(isOriginal);

    // ⛔ NOUGHT OR SEVERAL BOTH MEAN "ASK". They are not the same situation for a
    // person, but they are the same DECISION here, and collapsing them keeps the
    // callers from inventing a third behaviour between them.
    if (originals.length !== 1) {
        return {
            signTarget: null,
            printTarget: null,
            needsTheList: true,
            originals: originals.length,
            alreadySigned: false,
        };
    }

    const original = originals[0];

    /**
     * ⭐ THE NEWEST SIGNED COPY, not merely "a" signed copy. A document can be
     * signed more than once — a re-print after a correction is a real morning —
     * and printing the FIRST stamp when a later one exists would hand a driver
     * superseded paper while the system believed it had done the right thing.
     *
     * ⚠️ SORTED BY receivedUtc THEN documentId. Two copies made in the same
     * second would tie on the timestamp alone, and the tiebreak has to be a
     * column that cannot tie — the same rule the check-in panel's last-text query
     * had to learn (migration 142).
     */
    const signed = all
        .filter(d => d.signedFromDocumentId === original.documentId)
        .sort((a, b) => (a.receivedUtc === b.receivedUtc
            ? a.documentId.localeCompare(b.documentId)
            : a.receivedUtc.localeCompare(b.receivedUtc)));

    const newest = signed.length > 0 ? signed[signed.length - 1] : null;

    return {
        // ⚠️ STILL THE ORIGINAL. Signing again is allowed — a corrected stamp is a
        // real need — and it must stamp the original, not the previous stamp.
        signTarget: original,
        printTarget: newest ?? original,
        needsTheList: false,
        originals: 1,
        alreadySigned: newest !== null,
    };
}

/**
 * What the print button should SAY, so a scheduler knows what will come out
 * before pressing it.
 *
 * ⭐ NAMING THE TARGET IS THE POINT. "Print" on a card that holds a signed copy
 * and an original is the one press where being wrong is invisible until a driver
 * is already down the road with the wrong paper.
 */
export function printLabel(choice: PaperworkChoice): string {
    if (choice.needsTheList) {
        return choice.originals === 0
            ? 'Paperwork — nothing filed yet'
            : `Paperwork — ${choice.originals} documents, choose one`;
    }
    return choice.alreadySigned ? 'Print the signed copy' : 'Print the paperwork';
}

/** What the sign button should say, for the same reason. */
export function signLabel(choice: PaperworkChoice): string {
    if (choice.needsTheList) {
        return choice.originals === 0
            ? 'Nothing to sign yet'
            : `${choice.originals} documents — choose which to sign`;
    }
    return choice.alreadySigned ? 'Sign again' : 'Sign the paperwork';
}
